import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GoogleDriveImageSelectorComponent } from '../google-drive-image-selector/google-drive-image-selector.component';
import { GoogleDriveImage } from '../../../services/google-drive-images.service';
import { ToastService } from '../../../services/toast.service';
import { I18nService } from '../../../i18n/i18n.service';

export interface ImageUploadConfig {
  label: string;
  placeholder: string;
  hint: string;
  acceptTypes: string;
  maxSize?: number;
}

export interface PromptConfig {
  label: string;
  placeholder: string;
  rows: number;
  maxLength?: number;
}

export interface GenerateButtonConfig {
  defaultText: string;
  processingText: string;
}

@Component({
  selector: 'app-config-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, GoogleDriveImageSelectorComponent],
  templateUrl: './config-panel.component.html',
  styleUrls: ['./config-panel.component.scss']
})
export class ConfigPanelComponent {
  // === Input プロパティ ===
  
  // パネル基本設定
  @Input() title: string = '設定';
  @Input() isMinimized: boolean = false;
  
  // 画像アップロード設定
  @Input() imageUploadConfig: ImageUploadConfig = {
    label: '画像をアップロード',
    placeholder: '画像をドロップまたはクリック',
    hint: 'JPG, PNG, GIF (最大10MB)',
    acceptTypes: 'image/*',
    maxSize: 10
  };
  
  // プロンプト設定
  @Input() promptConfig: PromptConfig = {
    label: 'プロンプトを記述',
    placeholder: 'プロンプトを入力してください',
    rows: 4
  };
  
  // 生成ボタン設定
  @Input() buttonConfig: GenerateButtonConfig = {
    defaultText: '生成',
    processingText: '生成中...'
  };
  
  // 状態管理
  @Input() isProcessing: boolean = false;
  @Input() isGenerateDisabled: boolean = false;
  
  // === Output イベント ===
  
  @Output() fileSelected = new EventEmitter<File>();
  @Output() fileRemoved = new EventEmitter<void>();
  @Output() promptChanged = new EventEmitter<string>();
  @Output() generateClicked = new EventEmitter<void>();
  @Output() validationError = new EventEmitter<string>();
  
  // === 内部状態 ===
  
  uploadedImage: string | null = null;
  uploadedFile: File | null = null;
  promptValue: string = '';
  googleDriveSelectorOpen = false;
  
  constructor(private toastService: ToastService, public i18n: I18nService) {}
  
  // === メソッド ===
  
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      
      // ファイルサイズチェック
      if (!this.validateFileSize(file)) {
        this.validationError.emit(this.i18n.t('toast.fileSizeExceeded', { maxSize: String(this.imageUploadConfig.maxSize) }));
        return;
      }
      
      this.uploadedFile = file;
      
      // プレビュー画像生成
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        this.uploadedImage = e.target?.result as string;
      };
      reader.readAsDataURL(file);
      
      this.fileSelected.emit(file);
    }
  }
  
  onFileRemove(): void {
    this.uploadedImage = null;
    this.uploadedFile = null;
    this.fileRemoved.emit();
  }
  
  onPromptChange(value: string): void {
    this.promptValue = value;
    this.promptChanged.emit(value);
  }
  
  onGenerate(): void {
    this.generateClicked.emit();
  }

  // Google Drive 関連メソッド
  openGoogleDriveSelector(): void {
    this.googleDriveSelectorOpen = true;
  }

  onGoogleDriveImageSelected(event: { file: File; imageData: GoogleDriveImage }): void {
    const { file, imageData } = event;
    
    // ファイルサイズチェック
    if (!this.validateFileSize(file)) {
      this.validationError.emit(`ファイルサイズが${this.imageUploadConfig.maxSize}MBを超えています`);
      return;
    }
    
    this.uploadedFile = file;
    
    // プレビュー画像生成
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.uploadedImage = e.target?.result as string;
    };
    reader.readAsDataURL(file);
    
    this.fileSelected.emit(file);
    
    this.toastService.showToast(
      this.i18n.t('toast.imageSelected', { prompt: imageData.prompt.substring(0, 30) }),
      'success'
    );
  }

  onGoogleDriveSelectorClosed(): void {
    this.googleDriveSelectorOpen = false;
  }
  
  // === プライベートメソッド ===
  
  private validateFileSize(file: File): boolean {
    if (this.imageUploadConfig.maxSize) {
      const maxSizeBytes = this.imageUploadConfig.maxSize * 1024 * 1024;
      return file.size <= maxSizeBytes;
    }
    return true;
  }
}